import { useDroppable } from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { createSectionItemId } from '@griply/utils';
import { useCreateHabitAllowed } from 'features/tasks';
import { useUserHadTrial } from 'features/user';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { PremiumBanner } from 'shared/components/ui/premium-banner';
import { Habit } from 'shared/types/habit';
import { ID } from 'shared/types/id';
import { TaskPriorityOptions } from 'shared/types/task-base';
import { Timestamp } from 'shared/types/timestamp';

import { HabitsList } from '../habits-list';
import { SectionLayout } from '../section-layout';
import * as Styled from './habit-section.style';

export type HabitSectionProps = {
  habits: Habit[];
  weekDate: Timestamp;
  onHabit: (habit: Habit) => void;
  droppableId?: ID;
  goalId?: ID;
  lifeAreaId?: ID;
  priority?: TaskPriorityOptions;
  onPremium?: () => void;
};

export const HabitSection: React.FC<HabitSectionProps> = ({
  habits,
  weekDate,
  onHabit,
  droppableId,
  goalId,
  lifeAreaId,
  priority,
  onPremium,
}) => {
  const { t } = useTranslation();
  const userHadTrial = useUserHadTrial();
  const {
    enforced: createHabitLimitsEnforced,
    limit: createHabitLimits,
    total: totalHabits,
  } = useCreateHabitAllowed();

  const { setNodeRef } = useDroppable({
    id: droppableId ?? '',
    disabled: !droppableId || !!habits.length,
  });
  // A habit under two goals renders in both sections; each row gets its own sortable id.
  const itemIds = useMemo(
    () =>
      habits.map(({ id }) =>
        droppableId ? createSectionItemId(droppableId, id) : id,
      ),
    [habits, droppableId],
  );

  const containerProps = droppableId ? { ref: setNodeRef } : {};

  return (
    <Styled.Container {...containerProps}>
      <SectionLayout
        goalId={goalId}
        lifeAreaId={lifeAreaId}
        priority={priority}
      >
        <SortableContext items={itemIds} strategy={verticalListSortingStrategy}>
          <HabitsList
            items={habits}
            weekDate={weekDate}
            onHabit={onHabit}
            isSortable
            sectionId={droppableId}
          />
        </SortableContext>
      </SectionLayout>

      {onPremium && (
        <PremiumBanner
          title={t(
            createHabitLimitsEnforced
              ? 'habits.premium-banner.title'
              : 'habits.premium-banner.not-enforced.title',
            { total: totalHabits, limit: createHabitLimits },
          )}
          description={t(
            createHabitLimitsEnforced
              ? userHadTrial
                ? 'habits.premium-banner.description'
                : 'pages.habits.premium-banner.try-for-free.description'
              : 'habits.premium-banner.not-enforced.description',
            { limit: createHabitLimits },
          )}
          onClick={onPremium}
          isTrial={!userHadTrial}
        />
      )}
    </Styled.Container>
  );
};
